import React, { useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Calendar, Download } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { useQuery } from '@tanstack/react-query';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';
import { apiService } from '@/services/api';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
`;

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const hojeStr = () => new Date().toISOString().slice(0, 10);
const menosDias = (dias: number) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};

const PontoAtrasos: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [dataInicio, setDataInicio] = useState<string>(() => menosDias(7));
  const [dataFim, setDataFim] = useState<string>(() => hojeStr());
  const [todasFiliais, setTodasFiliais] = useState<boolean>(true);
  const [ordenarPor, setOrdenarPor] = useState<'minutos' | 'quantidade'>('minutos');
  const [direcao, setDirecao] = useState<'maiores' | 'menores'>('maiores');
  const [somenteComAtraso, setSomenteComAtraso] = useState<boolean>(true);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const isValidDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const enabled = isValidDate(dataInicio) && isValidDate(dataFim) && (todasFiliais || !!filialSelecionada?.id);

  const queryKey = useMemo(() => (
    ['ponto-atrasos', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dataInicio, dataFim, ordenarPor, direcao, somenteComAtraso ? '1' : '0']
  ), [todasFiliais, filialSelecionada?.id, dataInicio, dataFim, ordenarPor, direcao, somenteComAtraso]);

  const { data: resp, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    enabled,
    queryFn: async () => {
      const filtros = { dataInicio, dataFim, ordenarPor, direcao, somenteComAtraso };
      if (todasFiliais) return apiService.obterPontoAtrasosTodas(filtros);
      if (!filialSelecionada?.id) return null;
      return apiService.obterPontoAtrasosFilial(filialSelecionada.id, filtros);
    }
  });

  const colunas: string[] = resp?.data?.colunas || ['Filial','Nome','DiasComMarcacao','QtdeAtrasos','MinutosAtrasoTotal'];
  const linhas: any[] = resp?.data?.dados || [];

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas, colunas]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleExportar = () => {
    const rows = linhas || [];
    const cols = colunas || [];
    if (rows.length === 0 || cols.length === 0) return;
    const sep = ';';
    const header = cols.join(sep);
    const body = rows.map((linha) => cols.map((c) => String(linha[c] ?? '')).join(sep)).join('\n');
    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `ponto_ranking_atrasos_${contexto}_${dataInicio}_${dataFim}_${ordenarPor}_${direcao}.csv`;
    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <ReportHeader
        title="Ponto - Ranking de atrasos"
        subtitle={`${resp?.data?.totalRegistros ?? 0} registros`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || (linhas?.length ?? 0) === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={() => refetch()} disabled={!enabled || isLoading || isFetching}>
              <Calendar size={16} /> Aplicar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Ordenar por</Label>
          <Select value={ordenarPor} onChange={e => setOrdenarPor(e.target.value as any)}>
            <option value="minutos">Minutos de atraso (após 08:00)</option>
            <option value="quantidade">Quantidade de atrasos (após 08:10)</option>
          </Select>
        </Campo>
        <Campo>
          <Label>Direção</Label>
          <Select value={direcao} onChange={e => setDirecao(e.target.value as any)}>
            <option value="maiores">Maiores</option>
            <option value="menores">Menores</option>
          </Select>
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={somenteComAtraso}
              onChange={(e) => setSomenteComAtraso(e.target.checked)}
            />
            Somente quem teve atraso
          </CheckboxRow>
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
      </Filtros>

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>
      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {colunas.map((c) => (<Th key={c}>{c}</Th>))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={colunas.length}>Carregando...</Td></tr>
            ) : (linhas?.length ?? 0) === 0 ? (
              <tr><Td colSpan={colunas.length}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <tr key={idx}>
                  {colunas.map((c) => (<Td key={c}>{String(linha[c] ?? '')}</Td>))}
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default PontoAtrasos;

